import { useQuery, useMutation } from '@apollo/client';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as AcceptedIcon,
  HourglassEmpty as PendingIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  SENT_SHARES_QUERY,
  RECEIVED_SHARES_QUERY,
  PENDING_SHARES_QUERY,
  SENT_SHARE_REQUESTS_QUERY,
  PENDING_SHARE_REQUESTS_QUERY,
  REVOKE_SHARE_MUTATION,
  ACCEPT_DIRECT_SHARE_MUTATION,
  ACCEPT_SHARE_REQUEST_MUTATION,
  DECLINE_SHARE_REQUEST_MUTATION,
  CANCEL_SHARE_REQUEST_MUTATION,
} from '../services/graphql';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { VaultItemShare, ReceivedShare, ShareRequest } from '../types';

export default function Sharing() {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState(0);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const workspaceId = currentWorkspace?.id;

  // All shares are now workspace-scoped
  const { data: sentData, loading: sentLoading, refetch: refetchSentShares } = useQuery(SENT_SHARES_QUERY, {
    variables: { workspaceId },
    skip: !workspaceId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: receivedData, loading: receivedLoading, refetch: refetchReceivedShares } = useQuery(RECEIVED_SHARES_QUERY, {
    variables: { workspaceId },
    skip: !workspaceId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: pendingSharesData, loading: pendingSharesLoading, refetch: refetchPendingShares } = useQuery(PENDING_SHARES_QUERY, {
    variables: { workspaceId },
    skip: !workspaceId,
    fetchPolicy: 'cache-and-network',
  });

  // Workspace-scoped share requests
  const { data: sentShareRequestsData, loading: sentRequestsLoading, refetch: refetchSentRequests } = useQuery(
    SENT_SHARE_REQUESTS_QUERY,
    {
      variables: { workspaceId },
      skip: !workspaceId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: pendingShareRequestsData, loading: pendingRequestsLoading, refetch: refetchPendingRequests } = useQuery(
    PENDING_SHARE_REQUESTS_QUERY,
    {
      variables: { workspaceId },
      skip: !workspaceId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Refetch all queries when workspace changes
  useEffect(() => {
    if (workspaceId) {
      refetchSentShares({ workspaceId });
      refetchReceivedShares({ workspaceId });
      refetchPendingShares({ workspaceId });
      refetchSentRequests({ workspaceId });
      refetchPendingRequests({ workspaceId });
    }
  }, [workspaceId, refetchSentShares, refetchReceivedShares, refetchPendingShares, refetchSentRequests, refetchPendingRequests]);

  const [revokeShare, { loading: revokeLoading }] = useMutation(REVOKE_SHARE_MUTATION, {
    refetchQueries: workspaceId
      ? [{ query: SENT_SHARES_QUERY, variables: { workspaceId } }]
      : [],
    onCompleted: () => setSuccess('Share revoked'),
    onError: (err) => setError(err.message),
  });

  const [acceptShare, { loading: acceptLoading }] = useMutation(ACCEPT_DIRECT_SHARE_MUTATION, {
    refetchQueries: workspaceId
      ? [
          { query: PENDING_SHARES_QUERY, variables: { workspaceId } },
          { query: RECEIVED_SHARES_QUERY, variables: { workspaceId } },
        ]
      : [],
    onCompleted: () => setSuccess('Share accepted! You can now view the item.'),
    onError: (err) => setError(err.message),
  });

  const [acceptShareRequest, { loading: acceptRequestLoading }] = useMutation(
    ACCEPT_SHARE_REQUEST_MUTATION,
    {
      onCompleted: (data) => {
        if (data.acceptShareRequest.success) {
          setSuccess('Share request accepted! You can now view the item.');
          refetchPendingRequests();
        } else {
          setError(data.acceptShareRequest.message || 'Failed to accept share request');
        }
      },
      onError: (err) => setError(err.message),
    }
  );

  const [declineShareRequest, { loading: declineRequestLoading }] = useMutation(
    DECLINE_SHARE_REQUEST_MUTATION,
    {
      onCompleted: (data) => {
        if (data.declineShareRequest.success) {
          setSuccess('Share request declined');
          refetchPendingRequests();
        } else {
          setError(data.declineShareRequest.message || 'Failed to decline share request');
        }
      },
      onError: (err) => setError(err.message),
    }
  );

  const [cancelShareRequest, { loading: cancelRequestLoading }] = useMutation(
    CANCEL_SHARE_REQUEST_MUTATION,
    {
      onCompleted: (data) => {
        if (data.cancelShareRequest.success) {
          setSuccess('Share request cancelled');
          refetchSentRequests();
        } else {
          setError(data.cancelShareRequest.message || 'Failed to cancel share request');
        }
      },
      onError: (err) => setError(err.message),
    }
  );

  const handleRevoke = async (shareId: string) => {
    await revokeShare({
      variables: { input: { shareId } },
    });
  };

  const handleAcceptShare = async (shareId: string) => {
    await acceptShare({
      variables: { input: { shareId } },
    });
  };

  const handleAcceptShareRequest = async (shareRequestId: string) => {
    await acceptShareRequest({
      variables: { shareRequestId },
    });
  };

  const handleDeclineShareRequest = async (shareRequestId: string) => {
    await declineShareRequest({
      variables: { shareRequestId },
    });
  };

  const handleCancelShareRequest = async (shareRequestId: string) => {
    await cancelShareRequest({
      variables: { shareRequestId },
    });
  };

  const sentShares: VaultItemShare[] = sentData?.sentShares || [];
  const receivedShares: ReceivedShare[] = receivedData?.receivedShares || [];
  const pendingShares: ReceivedShare[] = pendingSharesData?.pendingShares || [];
  const sentShareRequests: ShareRequest[] = sentShareRequestsData?.sentShareRequests || [];
  const pendingShareRequests: ShareRequest[] = pendingShareRequestsData?.pendingShareRequests || [];

  // Calculate total pending items
  const totalPending = pendingShares.length + pendingShareRequests.length;

  // Combine sent shares and requests for display
  const allSentItems = [
    ...sentShares.map((s) => ({ type: 'legacy' as const, data: s })),
    ...sentShareRequests.map((s) => ({ type: 'request' as const, data: s })),
  ];

  return (
    <Box sx={{ flex: 1 }}>
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Sharing
            </Typography>
            {currentWorkspace && (
              <Typography variant="body2" color="text.secondary">
                Manage shared items in "{currentWorkspace.name}"
              </Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2 }}
          variant="fullWidth"
        >
          <Tab label={`Sent (${allSentItems.length})`} />
          <Tab label={`Received (${receivedShares.length})`} />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Pending
                {totalPending > 0 && (
                  <Chip size="small" label={totalPending} color="warning" sx={{ ml: 0.5, height: 20 }} />
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Sent Shares */}
        {activeTab === 0 && (
          <Paper sx={{ p: 2 }}>
            {sentLoading || sentRequestsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : allSentItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  You haven&apos;t shared any items yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Open an item and tap the share icon to share it with workspace members.
                </Typography>
              </Box>
            ) : (
              <List>
                {/* Legacy sent shares */}
                {sentShares.map((share) => (
                  <ListItem key={share.id} divider>
                    <ListItemText
                      primary={share.recipientEmail}
                      secondary={`Shared on ${new Date(share.sharedAt).toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        icon={share.status === 'accepted' ? <AcceptedIcon /> : <PendingIcon />}
                        label={share.status === 'accepted' ? 'Accepted' : 'Pending'}
                        color={share.status === 'accepted' ? 'success' : 'warning'}
                      />
                      <Tooltip title="Revoke share">
                        <IconButton
                          edge="end"
                          onClick={() => handleRevoke(share.id)}
                          disabled={revokeLoading}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {/* New share requests */}
                {sentShareRequests.map((request) => (
                  <ListItem key={request.id} divider>
                    <ListItemText
                      primary={request.receiverEmail}
                      secondary={`Sent on ${new Date(request.createdAt).toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        icon={
                          request.status === 'accepted' ? (
                            <AcceptedIcon />
                          ) : request.status === 'declined' ? (
                            <CloseIcon />
                          ) : (
                            <PendingIcon />
                          )
                        }
                        label={
                          request.status === 'accepted'
                            ? 'Accepted'
                            : request.status === 'declined'
                            ? 'Declined'
                            : request.status === 'expired'
                            ? 'Expired'
                            : 'Pending'
                        }
                        color={
                          request.status === 'accepted'
                            ? 'success'
                            : request.status === 'pending'
                            ? 'warning'
                            : 'default'
                        }
                      />
                      {request.status === 'pending' && (
                        <Tooltip title="Cancel request">
                          <IconButton
                            edge="end"
                            onClick={() => handleCancelShareRequest(request.id)}
                            disabled={cancelRequestLoading}
                            color="error"
                            size="small"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Received Shares (accepted only) */}
        {activeTab === 1 && (
          <Paper sx={{ p: 2 }}>
            {receivedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : receivedShares.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No items have been shared with you</Typography>
                {totalPending > 0 && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    You have {totalPending} pending share(s) to accept
                  </Typography>
                )}
              </Box>
            ) : (
              <List>
                {receivedShares.map((share) => (
                  <ListItem key={share.id} divider>
                    <ListItemText
                      primary={`From ${share.senderEmail}`}
                      secondary={`Shared on ${new Date(share.sharedAt).toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        onClick={() => navigate(`/vault/${share.itemId}`)}
                      >
                        View
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        )}

        {/* Pending - Shares and share requests to accept */}
        {activeTab === 2 && (
          <Paper sx={{ p: 2 }}>
            {pendingSharesLoading || pendingRequestsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : totalPending === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No pending shares</Typography>
              </Box>
            ) : (
              <>
                {/* Legacy pending direct shares */}
                {pendingShares.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Direct shares waiting for your approval
                    </Typography>
                    <List>
                      {pendingShares.map((share) => (
                        <ListItem key={share.id} divider>
                          <ListItemText
                            primary={`From ${share.senderEmail}`}
                            secondary={`Shared on ${new Date(share.sharedAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={acceptLoading ? <CircularProgress size={16} /> : <CheckIcon />}
                              onClick={() => handleAcceptShare(share.id)}
                              disabled={acceptLoading}
                            >
                              Accept
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {/* New pending share requests */}
                {pendingShareRequests.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: pendingShares.length > 0 ? 2 : 0 }}>
                      Share requests waiting for your approval
                    </Typography>
                    <List>
                      {pendingShareRequests.map((request) => (
                        <ListItem key={request.id} divider>
                          <ListItemText
                            primary={`From ${request.senderEmail}`}
                            secondary={`Sent on ${new Date(request.createdAt).toLocaleDateString()} • Expires ${new Date(request.expiresAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              startIcon={
                                declineRequestLoading ? <CircularProgress size={16} /> : <CloseIcon />
                              }
                              onClick={() => handleDeclineShareRequest(request.id)}
                              disabled={declineRequestLoading || acceptRequestLoading}
                            >
                              Decline
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={
                                acceptRequestLoading ? <CircularProgress size={16} /> : <CheckIcon />
                              }
                              onClick={() => handleAcceptShareRequest(request.id)}
                              disabled={acceptRequestLoading || declineRequestLoading}
                            >
                              Accept
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            )}
          </Paper>
        )}

        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          message={success}
        />
      </Container>
    </Box>
  );
}
